// src/data/questions/baQuestions.ts
// Business Associate Management Questions (Q37-Q40)

import { Question } from '../../types';

export const baQuestions: Question[] = [
  {
    id: 'Q37',
    section: 'ba',
    question: 'Do you maintain an inventory of all vendors/contractors with access to PHI?',
    type: 'single',
    options: [
      { value: 'comprehensive', label: 'Yes, comprehensive inventory with BAA status', description: '', points: 3 },
      { value: 'partial', label: 'Partial inventory', description: '', points: 1 },
      { value: 'no', label: 'No vendor inventory', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.308(b) - Business associate contracts',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "You must maintain a current inventory/list of all vendors and contractors who have access to PHI (or who may have access). The inventory should include: vendor name, service provided, date BAA signed, PHI access type (read-only, read-write, full access), and renewal/expiration date. This inventory serves multiple purposes: (1) ensures you don't accidentally forget a vendor needs a BAA, (2) helps you track renewal dates and avoid lapsed agreements, (3) provides OCR with a clear picture of your vendor ecosystem during audits. Without an inventory, you risk discovering vendors without BAAs, missing BAA renewals, and losing track of the scope of vendor access.",
      hipaaReference: "§164.308(b)(1) - Business Associate Contracts; implies need for vendor inventory/tracking. Status: Required (implicit; good practice). Effective Date: Privacy Rule (2003); reiterated in 2013 Omnibus Rule.",
      realWorldExample: "A clinic maintains a vendor inventory: 15 vendors listed, with BAA status and expiration dates. In Q4, they review the list and identify 3 BAAs expiring in Q1 of next year. They contact vendors in November to negotiate renewals, ensuring continuity. A hospital discovers an old Business Associate Agreement signed 10 years ago with a hosting vendor who no longer provides services. They identify this through annual inventory review and remove the vendor from the list. Another clinic doesn't maintain a vendor inventory. During OCR audit, they're asked to list all vendors with PHI access. They scramble to compile a list and discover 3 vendors they'd forgotten about who don't have BAAs. Automatic citations.",
      estimatedCost: {
        small: "$0 (DIY) - $2,000 (consultant time)",
        medium: "$2,000-4,000 (ongoing maintenance)",
        large: "$5,000-20,000/year (vendor management system)"
      },
      implementationSteps: [
        "Initial inventory: 2-4 weeks",
        "Ongoing maintenance: 1-2 hours/month"
      ],
      commonMistakes: [
        "Organizations without vendor inventory often discover 'forgotten' vendors without BAAs during OCR audits",
        "Vendor inventory is one of the first things OCR asks for; inability to produce it = immediate red flag",
        "Tracking vendor expiration dates prevents lapsed agreements",
        "Inventory facilitates vendor management and risk assessment"
      ],
      resourceLinks: [
        { title: "HHS Business Associate Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "2-4 weeks",
        medium: "2-4 weeks",
        large: "2-4 weeks"
      }
    }
  },
  {
    id: 'Q38',
    section: 'ba',
    question: 'Do your Business Associate Agreements (BAAs) include subcontractor flow-down requirements?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, BAAs require subcontractors to sign BAAs', description: '', points: 3 },
      { value: 'some', label: 'Some BAAs have this, not all', description: '', points: 1 },
      { value: 'no', label: 'No flow-down provisions', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'Subcontractor BAA requirements',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "A subcontractor is a vendor hired by your direct vendor (Business Associate). For example: you hire a hosting company (BA), the hosting company hires a disaster recovery vendor (subcontractor). Subcontractors must also be bound by HIPAA obligations. Your BAA with the hosting company must require that the hosting company ensure subcontractors sign BAAs as well ('flow-down' requirements). Without flow-down language, your vendor can hire anyone without HIPAA obligations, creating compliance gaps. You must have language in your BAAs stating: 'You must ensure all your subcontractors sign BAAs with equivalent terms.'",
      hipaaReference: "§164.308(b)(2) - Subcontractor Requirements; BAAs must address subcontractor management. Status: Required. Effective Date: HITECH Act (2009); clarified in 2013 Omnibus Rule.",
      realWorldExample: "A clinic signs a BAA with a cloud hosting vendor. The BAA includes: 'You must ensure all subcontractors who access PHI sign BAAs with equivalent terms to this agreement.' The hosting vendor then hires a disaster recovery company; the clinic receives notice that the disaster recovery company also signed a BAA. Compliant. A hospital signs a BAA with a billing vendor without subcontractor language. The billing vendor then hires a collections agency to pursue unpaid bills. The collections agency doesn't have a BAA. Clinic discovers this during an audit. Automatic violation; must immediately terminate the collections agency's access or require a retroactive BAA.",
      estimatedCost: {
        small: "$500-2,000 (BAA template with subcontractor language)",
        medium: "$0-2,000 (subcontractor matrix/tracking)",
        large: "$1,000-2,000 (annual audit of subcontractors)"
      },
      implementationSteps: [
        "BAA review and update: 1-2 weeks",
        "Subcontractor matrix creation: 1-2 weeks",
        "Ongoing tracking: 1-2 hours/quarter"
      ],
      commonMistakes: [
        "Vendor management chains can get long (you → vendor A → vendor B → vendor C); each link must have HIPAA obligations",
        "If a subcontractor breaches and doesn't have a BAA, your vendor can claim 'we didn't know HIPAA applied'",
        "You're liable for your vendor's subcontractors' breaches; you need contractual assurance they're HIPAA-compliant"
      ],
      resourceLinks: [
        { title: "HHS Business Associate Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "1-2 weeks",
        medium: "1-2 weeks",
        large: "1-2 weeks"
      }
    }
  },
  {
    id: 'Q39',
    section: 'ba',
    question: 'Do you audit your business associates\' HIPAA compliance?',
    type: 'single',
    options: [
      { value: 'regular', label: 'Yes, regular audits (at least annually)', description: '', points: 3 },
      { value: 'occasional', label: 'Occasional audits', description: '', points: 2 },
      { value: 'no', label: 'No audits of business associates', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: 'Business associate oversight',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "You must periodically audit (or have the right to audit) your vendors' HIPAA compliance. Audits assess: Do they have the security controls promised in the BAA? Are they logging access? Is their facility secure? Do they handle breaches appropriately? Audits can be: (1) on-site (you visit their facility and review controls), (2) remote (review documentation, test controls remotely), or (3) third-party (vendor undergoes SOC 2 audit and you review results). At minimum, you should conduct annual audits or review third-party audit results. For critical vendors (cloud providers, EHR, major vendors), audits should be more frequent.",
      hipaaReference: "§164.308(b)(2) - Oversight of Business Associates; implies need for auditing/monitoring. Status: Required (to oversee vendor compliance). Effective Date: HITECH Act (2009); clarified in 2013 Omnibus Rule.",
      realWorldExample: "A clinic's cloud provider undergoes a SOC 2 Type II audit annually. The clinic reviews the audit report and confirms security controls are in place. This counts as an audit requirement. A hospital has 20 major vendors. They conduct annual remote audits (questionnaires) for most, and on-site audits every 3 years for critical vendors (EHR, cloud). A small clinic can't afford to audit vendors directly; instead, they request that all vendors provide SOC 2 or HITRUST reports. If vendors can't provide these, the clinic doesn't work with them. This ensures some level of independent verification.",
      estimatedCost: {
        small: "$500-1,000 (reviewing SOC 2/HITRUST reports)",
        medium: "$2,000-4,000 (remote audit program)",
        large: "$5,000-20,000 per vendor (on-site audit with consultant)"
      },
      implementationSteps: [
        "Audit program development: 2-3 weeks",
        "Annual audit execution: varies (remote = 1-2 weeks; on-site = 2-4 weeks per vendor)"
      ],
      commonMistakes: [
        "Most data breaches involve vendors; auditing reduces risk significantly",
        "You're liable for vendor breaches; audits are your defense against 'we didn't know'",
        "OCR specifically asks about vendor audits; lack of audits is a major red flag",
        "Vendors with audit results are more trustworthy and more likely to catch their own compliance issues"
      ],
      resourceLinks: [
        { title: "HHS Business Associate Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "1-2 weeks",
        medium: "2-4 weeks",
        large: "4-8 weeks"
      }
    }
  },
  {
    id: 'Q40',
    section: 'ba',
    question: 'Does your cloud provider (AWS/Azure/GCP) have a signed BAA?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, signed BAA with cloud provider', description: '', points: 3 },
      { value: 'no', label: 'No BAA with cloud provider', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
      { value: 'na', label: 'Not applicable - no cloud provider', description: '', points: 3 },
    ],
    regulation: 'AWS, Azure, GCP BAA requirements',
    required: false,
    conditionalOn: { questionId: 'Q4', requiredValue: 'cloud' },
    weight: 3,
    tooltipContent: {
      plainEnglish: "If you store/process PHI in a cloud environment (AWS, Azure, Google Cloud), the cloud provider must sign a HIPAA-compliant BAA. All three major cloud providers offer HIPAA BAAs. However, not all cloud services are covered by the BAA; you must verify that the specific services you use (e.g., S3, Redshift, Lambda) are 'HIPAA-eligible services.' A cloud provider's BAA typically covers their baseline security responsibilities: encryption, access controls, audit logging. You remain responsible for additional security measures: encryption key management, application-level access controls, security configuration.",
      hipaaReference: "§164.308(b)(1) - Business Associate Contracts; applies to cloud providers. Status: Required if you use cloud services with PHI. Effective Date: Privacy Rule (2003); HITECH Act clarified BA requirements (2009).",
      realWorldExample: "A clinic stores patient records in AWS S3 (a HIPAA-eligible service). They contact AWS, execute a HIPAA BAA, and proceed. Compliant. Another clinic uses AWS Lambda for a custom application that processes PHI. They verify Lambda is HIPAA-eligible (it is). They execute a BAA and proceed. A third clinic uses AWS DynamoDB (NoSQL database) to store PHI. They check the HIPAA-eligible services list and discover DynamoDB is NOT listed. They either: (1) switch to a HIPAA-eligible database (RDS), or (2) encrypt PHI before sending to DynamoDB (application-level encryption, so even if DynamoDB isn't HIPAA-eligible, the data is protected).",
      estimatedCost: {
        small: "$0 (cloud provider HIPAA BAA is standard process)",
        medium: "$5,000-20,000 (HIPAA-eligible services architecture design)",
        large: "$500-5,000/month (ongoing cloud security)"
      },
      implementationSteps: [
        "BAA execution: 1-4 weeks (AWS/Azure usually fast; Google may be slower)",
        "Architecture review: 2-4 weeks",
        "Implementation: 4-12 weeks (depends on complexity)"
      ],
      commonMistakes: [
        "Using cloud services without a BAA is an automatic HIPAA violation",
        "Cloud provider BAAs are critical because cloud is often where healthcare data lives",
        "Not all cloud services are covered; verify the specific services in your architecture",
        "BAAs with cloud providers are easy to obtain (standard process for all three major clouds)"
      ],
      resourceLinks: [
        { title: "HHS Business Associate Requirements", url: "https://www.hhs.gov/hipaa/for-professionals/privacy/guidance/business-associates/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "1-4 weeks",
        medium: "4-8 weeks",
        large: "8-16 weeks"
      }
    }
  },
];
